import React from 'react';
import round from 'lodash/round';
import GeneralChart from '@/components/seo/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { SALARY_RANGES } from '@/constants/job-title';
import { JobTitleType } from '@/types/job-title';

const SalaryOverview = (props: {
  salaryData: Record<string, any>;
  salaryDetails: JobTitleType['salary_details'];
  jobOpenDetails: JobTitleType['job_open_details'];
  id?: string;
  border?: string;
  margin?: string;
  avgSalaryTitle?: React.ReactNode;
  handleClick?: () => void;
}) => {
  const {
    salaryData,
    salaryDetails: {
      avg,
      min,
      max,
      avg_median,
      distribution_details: distributionDetails,
    },
    jobOpenDetails: { current_count: currentCount },
    id,
    border,
    margin,
    avgSalaryTitle = '平均月薪',
    handleClick,
  } = props;
  const barChartRef = React.useRef(null);

  const barConfig = React.useMemo<ECOption & { rangeText: string }>(() => {
    const totalCount = Object.values(distributionDetails).reduce(
      (a, b) => a + b,
      0,
    );

    const data: Record<string, number> = {};
    SALARY_RANGES.forEach((item) => {
      data[item] = distributionDetails[item] || 0;
    });

    const barData = SALARY_RANGES.map((range) => ({
      category: range,
      value: Number(((data[range] / totalCount) * 100).toFixed(1)) || 0,
    })).reverse();

    const maxItem = barData.reduce(
      (max, item) => (item.value > max.value ? item : max),
      barData[0],
    );

    const rangeText = maxItem
      ? `${maxItem.value}% 人群薪酬落在 ${maxItem.category}`
      : '';

    return {
      rangeText,
      grid: { show: false, top: 0, bottom: 0, left: 60, right: 55 },
      xAxis: {
        type: 'value',
        min: '0',
        max: '100',
        show: false,
      },
      yAxis: [
        {
          type: 'category',
          offset: 50,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#2d2d2d',
            fontSize: 14,
            align: 'left',
          },
          data: barData.map((item) => item.category),
        },
        {
          type: 'category',
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            formatter: (value: string) => `${value}%`,
            color: '#6e6e6e',
            fontSize: 14,
          },
          data: barData.map((item) => item.value),
        },
      ],
      series: [
        {
          type: 'bar',
          data: barData,
          showBackground: true,
          backgroundStyle: {
            color: '#eee',
            borderRadius: 6,
          },
          barWidth: 6,
          cursor: 'default',
          itemStyle: {
            color: '#3f73d3',
            borderRadius: 6,
          },
        },
      ],
    };
  }, [distributionDetails]);

  return (
    <GeneralChart
      id={id}
      padding="p-0"
      margin={margin}
      border={border}
      onClick={handleClick}
    >
      <div className="grid grid-cols-2">
        <div className="pl-10 pr-6 pt-8 pb-4 border-r border-white-smoke flex flex-col">
          <h3 className="font-semibold">{avgSalaryTitle}</h3>
          <p className="text-4.5xl font-semibold mt-2">{`¥${round(avg, -2)}`}</p>
          <p className="text-sm mt-2">{`中位数 ¥${round(avg_median || 0, -2)} ｜ 区间 ¥${round(min, -2)} - ¥${round(max, -2)}`}</p>
          <p className="text-sm mt-4">{salaryData['趋势说明'] || ''}</p>
          <p className="text-xs text-web-grey-600 mt-auto">{`来自全网 ${currentCount} 份数据`}</p>
        </div>
        <div className="px-10 py-8">
          <h3 className="font-semibold">月薪分布</h3>
          <p className="text-sm mt-2">{barConfig.rangeText}</p>
          <div className="h-32 mt-4">
            <CusEcharts chartRef={barChartRef} config={barConfig} />
          </div>
        </div>
      </div>
    </GeneralChart>
  );
};

export default SalaryOverview;
